import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createApiUrl } from '../utils/apiConfig';
import styles from './StudentProgressMonitoring.module.css';

const StudentProgressMonitoring = ({ student }) => {
  const [missions, setMissions] = useState([]);
  const [assignedLessons, setAssignedLessons] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        // Fetch missions, assigned lessons, and quiz assignments for this student
        const [missionsResponse, lessonsResponse, quizAssignmentsResponse] = await Promise.all([
          axios.get(createApiUrl(`/api/teacher/student/${student._id}/missions`), {
            withCredentials: true
          }),
          axios.get(createApiUrl(`/api/lesson-assignments/teacher/student/${student._id}`), {
            withCredentials: true
          }),
          axios.get(createApiUrl(`/api/quiz-assignments/teacher/student/${student._id}`), {
            withCredentials: true
          })
        ]);

        if (missionsResponse.data.success) {
          // Sort missions by assignedAt date (newest first)
          const sortedMissions = missionsResponse.data.data.sort((a, b) => {
            const dateA = new Date(a.assignedAt || a.createdAt || 0);
            const dateB = new Date(b.assignedAt || b.createdAt || 0);
            return dateB - dateA; // Newest first
          });
          setMissions(sortedMissions);
        }

        if (lessonsResponse.data.success) {
          // Sort lessons by assignedDate (newest first)
          const sortedLessons = lessonsResponse.data.data.sort((a, b) => {
            const dateA = new Date(a.assignedDate || 0);
            const dateB = new Date(b.assignedDate || 0);
            return dateB - dateA; // Newest first
          });
          setAssignedLessons(sortedLessons);
        }

        if (quizAssignmentsResponse.data.success) {
          // Process quiz assignments and sort by assignedDate (newest first)
          const studentQuizAssignments = quizAssignmentsResponse.data.data
            .map(assignment => ({
              _id: assignment._id,
              title: assignment.quizId?.title || 'Unknown Quiz',
              description: assignment.quizId?.description,
              subject: assignment.quizId?.subject,
              gradeLevel: assignment.quizId?.gradeLevel,
              difficulty: assignment.quizId?.difficulty,
              totalPoints: assignment.quizId?.totalPoints || 0,
              estimatedDuration: assignment.quizId?.estimatedDuration,
              questions: assignment.quizId?.questions || [],
              assignmentStatus: assignment.status || 'assigned',
              assignedAt: assignment.assignedDate || assignment.createdAt,
              completedAt: assignment.completedDate,
              score: assignment.score,
              percentage: assignment.score, // Score is already a percentage
              dueDate: assignment.dueDate,
              attempts: assignment.attempts || 0,
              notes: assignment.notes
            }))
            .sort((a, b) => {
              const dateA = new Date(a.assignedAt || 0);
              const dateB = new Date(b.assignedAt || 0);
              return dateB - dateA; // Newest first
            });
          setQuizzes(studentQuizAssignments);
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (student) {
      fetchStudentData();
    }
  }, [student]);

  if (loading) {
    return <div className={styles.loading}>Loading student progress...</div>;
  }

  return (
    <div className={styles.monitoringContainer}>
      <h3 className={styles.monitoringTitle}>
        Progress for {student.contactName}
      </h3>
      
      <div className={styles.studentInfo}>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Student Code:</span>
          <span className={styles.infoValue}>{student.studentCode}</span>
        </div>
        {(student.yearGrade || student.grade || student.Grade || student.yearGroup) && (
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Year Group:</span>
            <span className={styles.infoValue}>{student.yearGrade || student.grade || student.Grade || student.yearGroup}</span>
          </div>
        )}
      </div>

      {/* Three Column Layout for Missions, Lessons, and Quizzes */}
      <div className={styles.threeColumnLayout}>
        {/* Missions Column */}
        <div className={styles.column}>
          <h4 className={styles.missionsTitle}>Assigned Missions</h4>
          {missions.length === 0 ? (
            <div className={styles.noMissions}>
              <p>No missions assigned to this student yet.</p>
            </div>
          ) : (
            <div className={styles.missionsList}>
              {missions.map((mission) => (
                <div key={mission._id} className={styles.missionCard}>
                  <div className={styles.missionHeader}>
                    <h5 className={styles.missionTitle}>{mission.title}</h5>
                    <span className={`${styles.statusBadge} ${styles[mission.status]}`}>
                      {mission.status}
                    </span>
                  </div>
                  
                  <div className={styles.missionDetails}>
                    {typeof mission.progress === 'number' && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Progress:</span>
                        <span className={styles.detailValue}>{mission.progress}%</span>
                      </div>
                    )}
                    
                    {mission.progressData && mission.progressData.completedTasks && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Tasks Completed:</span>
                        <span className={styles.detailValue}>
                          {mission.progressData.completedTasks.length}/4
                        </span>
                      </div>
                    )}
                    
                    {mission.score > 0 && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Score:</span>
                        <span className={styles.detailValue}>{mission.score} points</span>
                      </div>
                    )}
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Viewed:</span>
                      <span className={styles.detailValue}>
                        {mission.viewed ? 'Yes' : 'No'}
                      </span>
                    </div>
                    
                    {mission.viewedAt && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Viewed At:</span>
                        <span className={styles.detailValue}>
                          {new Date(mission.viewedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {mission.lastSaved && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Last Saved:</span>
                        <span className={styles.detailValue}>
                          {new Date(mission.lastSaved).toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {mission.completedAt && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Completed At:</span>
                        <span className={styles.detailValue}>
                          {new Date(mission.completedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {typeof mission.progress === 'number' && (
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ width: `${mission.progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lessons Column */}
        <div className={styles.column}>
          <h4 className={styles.lessonsTitle}>Assigned Lessons</h4>
          {assignedLessons.length === 0 ? (
            <div className={styles.noLessons}>
              <p>No lessons assigned to this student yet.</p>
            </div>
          ) : (
            <div className={styles.lessonsList}>
              {assignedLessons.map((lesson) => (
                <div key={lesson._id} className={styles.lessonCard}>
                  <div className={styles.lessonHeader}>
                    <h5 className={styles.lessonTitle}>{lesson.lessonId?.name || ''}</h5>
                    <span className={`${styles.statusBadge} ${styles[lesson.status]}`}>
                      {lesson.status}
                    </span>
                  </div>
                  
                  <div className={styles.lessonDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Status:</span>
                      <span className={styles.detailValue}>{lesson.status}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Assigned Date:</span>
                      <span className={styles.detailValue}>
                        {new Date(lesson.assignedDate).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {lesson.completedDate && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Completed Date:</span>
                        <span className={styles.detailValue}>
                          {new Date(lesson.completedDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    {lesson.score !== undefined && lesson.score !== null && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Score:</span>
                        <span className={styles.detailValue}>{lesson.score} points</span>
                      </div>
                    )}
                    
                    {lesson.notes && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Notes:</span>
                        <span className={styles.detailValue}>{lesson.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quizzes Column */}
        <div className={styles.column}>
          <h4 className={styles.quizzesTitle}>Assigned Quizzes</h4>
          {quizzes.length === 0 ? (
            <div className={styles.noQuizzes}>
              <p>No quizzes assigned to this student yet.</p>
            </div>
          ) : (
            <div className={styles.quizzesList}>
              {quizzes.map((quiz) => (
                <div key={quiz._id} className={styles.quizCard}>
                  <div className={styles.quizHeader}>
                    <h5 className={styles.quizTitle}>{quiz.title}</h5>
                    <span className={`${styles.statusBadge} ${styles[quiz.assignmentStatus]}`}>
                      {quiz.assignmentStatus}
                    </span>
                  </div>
                  
                  <div className={styles.quizDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Status:</span>
                      <span className={styles.detailValue}>{quiz.assignmentStatus}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Assigned Date:</span>
                      <span className={styles.detailValue}>
                        {new Date(quiz.assignedAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {quiz.dueDate && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Due Date:</span>
                        <span className={styles.detailValue}>
                          {new Date(quiz.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    {quiz.completedAt && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Completed Date:</span>
                        <span className={styles.detailValue}>
                          {new Date(quiz.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    {quiz.score !== undefined && quiz.score !== null && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Score:</span>
                        <span className={styles.detailValue}>{quiz.score}%</span>
                      </div>
                    )}
                    
                    {quiz.attempts > 0 && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Attempts:</span>
                        <span className={styles.detailValue}>{quiz.attempts}</span>
                      </div>
                    )}
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Subject:</span>
                      <span className={styles.detailValue}>{quiz.subject || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Grade Level:</span>
                      <span className={styles.detailValue}>{quiz.gradeLevel || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Difficulty:</span>
                      <span className={styles.detailValue}>{quiz.difficulty || 'N/A'}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Total Questions:</span>
                      <span className={styles.detailValue}>{quiz.questions?.length || 0}</span>
                    </div>
                    
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Total Points:</span>
                      <span className={styles.detailValue}>{quiz.totalPoints || 0}</span>
                    </div>
                    
                    {quiz.estimatedDuration && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Duration:</span>
                        <span className={styles.detailValue}>{quiz.estimatedDuration} minutes</span>
                      </div>
                    )}
                    
                    {quiz.notes && (
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Notes:</span>
                        <span className={styles.detailValue}>{quiz.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProgressMonitoring; 